import { API_BASE } from "./base";
import type { DocumentStatus } from "../types";

export type DocumentRealtimeAction =
  | "document_uploaded"
  | "document_reuploaded"
  | "document_superseded"
  | "document_extracted"
  | "document_edited"
  | "document_approved"
  | "document_rejected"
  | "document_fix_requested"
  | "document_amended";

export interface DocumentRealtimeEvent {
  type: "document.changed";
  action: DocumentRealtimeAction;
  documentId: string;
  status: DocumentStatus;
  occurredAt: string;
}

export type RealtimeEvent = DocumentRealtimeEvent | { type: "realtime.ready"; occurredAt: string };

export function subscribeToDocumentRealtime(
  accessToken: string,
  onEvent: (event: RealtimeEvent) => void,
): () => void {
  let closedByCaller = false;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let attempt = 0;
  let socket: WebSocket | null = null;

  const connect = () => {
    socket = new WebSocket(documentRealtimeUrl(), ["orderflow", `bearer.${accessToken}`]);

    socket.onopen = () => {
      attempt = 0;
    };

    socket.onmessage = (message) => {
      const event = parseRealtimeEvent(message.data);
      if (event) onEvent(event);
    };

    socket.onclose = () => {
      if (closedByCaller) return;
      const delayMs = Math.min(10_000, 500 * 2 ** attempt);
      attempt += 1;
      reconnectTimer = setTimeout(connect, delayMs);
    };

    socket.onerror = () => {
      socket?.close();
    };
  };

  connect();

  return () => {
    closedByCaller = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    socket?.close();
  };
}

function documentRealtimeUrl(): string {
  const base = new URL(API_BASE.endsWith("/") ? API_BASE : `${API_BASE}/`, window.location.origin);
  base.protocol = base.protocol === "https:" ? "wss:" : "ws:";
  base.pathname = `${base.pathname.replace(/\/$/, "")}/realtime/documents`;
  base.search = "";
  base.hash = "";
  return base.toString();
}

function parseRealtimeEvent(raw: unknown): RealtimeEvent | null {
  if (typeof raw !== "string") return null;
  try {
    const parsed = JSON.parse(raw) as Partial<RealtimeEvent>;
    if (parsed.type === "realtime.ready" && typeof parsed.occurredAt === "string") {
      return { type: "realtime.ready", occurredAt: parsed.occurredAt };
    }
    if (
      parsed.type === "document.changed" &&
      typeof parsed.documentId === "string" &&
      typeof parsed.action === "string" &&
      typeof parsed.status === "string" &&
      typeof parsed.occurredAt === "string"
    ) {
      return parsed as DocumentRealtimeEvent;
    }
  } catch {
    return null;
  }
  return null;
}
